import { useEffect, useState } from "react";
import { RightWrapper } from "../../../components";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components";
import { useSelector } from "react-redux";
import { useApi } from "@/";
import { BiSolidEditAlt } from "react-icons/bi";
import { RiFileExcel2Line } from "react-icons/ri";
import { FaPlus } from "react-icons/fa";
import { HiOutlineUpload } from "react-icons/hi";
import UpdateRateChart from "./UpdateRateChart";
import { useNavigate } from "react-router";
const weightBrackets = [
  "0.5 Kg",
  "1 Kg",
  "1.5 Kg",
  "2 Kg",
  "2.5 Kg",
  "3 Kg",
  "3.5 Kg",
  "4 Kg",
  "4.5 Kg",
  "5 Kg",
  "6 Kg",
  "7 Kg",
  "8 Kg",
  "9 Kg",
  "10 Kg",
  "10+1 Kg",
];

const courierPartners = [
  "Xpress Bees",
  "Delhivery",
  "Bluedart",
  "Ecom Express",
  "Shadowfax",
];

const generateData = (apiData) => {
  // Group data by courierId and mode
  const groupedByCourier = apiData.reduce((acc, item) => {
    if (!acc[item.courierId]) acc[item.courierId] = { surface: [], air: [] };
    acc[item.courierId][item.mode.toLowerCase()].push(item);
    return acc;
  }, {});

  // Transform data into the desired structure with sorted weights
  const transformedData = Object.keys(groupedByCourier).map((courierId) => ({
    title: courierId,
    mode: {
      surface: groupedByCourier[courierId].surface
        .sort((a, b) => a.weightBracket - b.weightBracket)
        .map((entry) => ({
          weight: entry.weightBracket,
          zone1: entry.zoneRates.zone1,
          zone2: entry.zoneRates.zone2,
          zone3: entry.zoneRates.zone3,
          zone4: entry.zoneRates.zone4,
          zone5: entry.zoneRates.zone5,
          zone6: entry.zoneRates.zone6,
          cod: `${(entry.codPercentage * 100).toFixed(1)}% - ${entry.codFixed}`,
          gst: `${(entry.gst * 100).toFixed(0)}%`,
        })),
      air: groupedByCourier[courierId].air
        .sort((a, b) => a.weightBracket - b.weightBracket)
        .map((entry) => ({
          weight: entry.weightBracket,
          zone1: entry.zoneRates.zone1,
          zone2: entry.zoneRates.zone2,
          zone3: entry.zoneRates.zone3,
          zone4: entry.zoneRates.zone4,
          zone5: entry.zoneRates.zone5,
          zone6: entry.zoneRates.zone6,
          cod: `${(entry.codPercentage * 100).toFixed(1)}% - ${entry.codFixed}`,
          gst: `${(entry.gst * 100).toFixed(0)}%`,
        })),
    },
  }));

  return transformedData;
};

const RateChart = () => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const { callApi } = useApi();
  const [data, setData] = useState([]);
  const courier = useSelector((state) => state.app?.appData?.courier);
  const fetchPricing = async () => {
    try {
      const response = await callApi("admin/getRates", "GET");
      if (response.success) {
        // Handle successful pricing data fetch
        // console.log("Pricing data fetched successfully:", response.data);
        setData(response.data);
      } else {
        // console.error("Failed to fetch pricing data:", response.error.message);
      }
    } catch ({ error }) {
      console.error("Error fetching pricing data:", error);
    }
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const [dataSections, setDataSections] = useState([]);

  useEffect(() => {
    setDataSections(generateData(data || []));
  }, [data]);
  const navigate = useNavigate();
  const handleView = () => {
    navigate("edit");
  };
  //const dataSections = courierPartners.map(generateData(data));
  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        <div className="flex flex-col md:flex-row lg:justify-between items-end gap-6 lg:gap-2">
          <h1 className="text-headline4 md:text-headline3 font-nunito text-secondary/95 font-semibold">
            Rate Chart
          </h1>
          <div className="flex gap-1 ">
            <Button
              size="xs"
              icon={BiSolidEditAlt}
              onClick={() => handleView({})}
            >
              Edit Rate
            </Button>
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button size="xs" icon={HiOutlineUpload}>
                  Update All Rates
                </Button>
              </SheetTrigger>
              <SheetContent aria-describedby="">
                <SheetHeader>
                  <SheetTitle>Update All Rates</SheetTitle>
                </SheetHeader>
                <UpdateRateChart setSheetOpen={setSheetOpen} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
        <div className=" bg-background_2 !rounded-xl shadow-lg overflow-hidden mt-4 ">
          <Table className="w-full bg-background_2 rounded-xl">
            <TableHeader>
              <TableRow className="bg-background_2 ">
                <TableHead
                  rowSpan={2}
                  className="text-center border border-secondary/30 text-secondary/90 font-semibold"
                >
                  Courier Partner
                </TableHead>
                <TableHead
                  rowSpan={2}
                  className="text-center border border-secondary/30 text-secondary/90 font-semibold"
                >
                  Mode
                </TableHead>
                <TableHead
                  rowSpan={2}
                  className="text-center border border-secondary/30 text-secondary/90 font-semibold"
                >
                  Weight
                </TableHead>
              </TableRow>
              <TableRow className="bg-background_2 text-gray-700">
                <TableHead className="border border-secondary/30 text-secondary/90 font-semibold">
                  Zone 1 (Within City)
                </TableHead>
                <TableHead className="border border-secondary/30 text-secondary/90 font-semibold">
                  Zone 2 (Within State)
                </TableHead>
                <TableHead className="border border-secondary/30 text-secondary/90 font-semibold">
                  Zone 3 (Within Zone)
                </TableHead>
                <TableHead className="border border-secondary/30 text-secondary/90 font-semibold">
                  Zone 4 (Metro to Metro)
                </TableHead>
                <TableHead className="border border-secondary/30 text-secondary/90 font-semibold">
                  Zone 5 (Special Location)
                </TableHead>
                <TableHead className="border border-secondary/30 text-secondary/90 font-semibold">
                  Zone 6 (Rest of India)
                </TableHead>
                <TableHead className="border border-secondary/30 text-secondary/90 font-semibold">
                  COD
                </TableHead>
                <TableHead className="border border-secondary/30 text-secondary/90 font-semibold">
                  GST
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dataSections.map((section, index) =>
                Object.entries(section.mode).map(([mode, data], modeIndex) =>
                  data.map((row, rowIndex) => (
                    <TableRow
                      key={`${index}-${modeIndex}-${rowIndex}`}
                      className="odd:bg-background/50 even:bg-background"
                    >
                      {modeIndex === 0 && rowIndex === 0 && (
                        <TableCell
                          rowSpan={Object.values(section.mode).flat().length}
                          className="text-center font-bold border border-secondary/30 p-2 bg-background_2 text-secondary/90"
                        >
                          {courier?.find((c) => c._id === section.title)?.name}
                        </TableCell>
                      )}
                      {rowIndex === 0 && (
                        <TableCell
                          rowSpan={data.length}
                          className="text-center font-semibold capitalize border border-secondary/30 p-2 bg-background_2/30 text-secondary/80"
                        >
                          {mode}
                        </TableCell>
                      )}
                      <TableCell className="border border-secondary/30 p-2 text-center">
                        {row.weight === 11 ? "10+1 Kg" : `${row.weight} kg`}
                      </TableCell>
                      <TableCell className="border border-secondary/30 p-2 text-center">
                        {row.zone1}
                      </TableCell>
                      <TableCell className="border border-secondary/30 p-2 text-center">
                        {row.zone2}
                      </TableCell>
                      <TableCell className="border border-secondary/30 p-2 text-center">
                        {row.zone3}
                      </TableCell>
                      <TableCell className="border border-secondary/30 p-2 text-center">
                        {row.zone4}
                      </TableCell>
                      <TableCell className="border border-secondary/30 p-2 text-center">
                        {row.zone5}
                      </TableCell>
                      <TableCell className="border border-secondary/30 p-2 text-center">
                        {row.zone6}
                      </TableCell>
                      <TableCell className="border border-secondary/30 p-2 text-center min-w-20">
                        {row.cod}
                      </TableCell>
                      <TableCell className="border border-secondary/30 p-2 text-center">
                        {row.gst}
                      </TableCell>
                    </TableRow>
                  ))
                )
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col gap-1 w-full">
          <h1 className="text-headline4 md:text-headline3 font-nunito text-secondary/95 font-semibold">
            Terms & Conditions
          </h1>
          <ul className="text-body2 font-ptsans text-secondary/60 lg:w-[80%] list-disc pl-4 space-y-2">
            <li>18% GST will be charged extra on above rates.</li>
            <li>
              Chargeable Weight will be Physical / Dead Weight or Volumetric
              Weight whichever is Higher.
            </li>
            <li>
              COD Charges will be fixed COD charge or COD % of the order value
              whichever is Higher.
            </li>
            <li>
              Volumetric Weight (Kilogram) calculation as per IATA - Length (cm)
              x Breadth (cm) x Height (cm) / 5000
            </li>
            <li>
              Other Charges like Octroi charges, state entry tax and fees,
              address correction charges if applicable shall be charged extra.
            </li>
            <li>
              RTO (return to origin) shipment will be charged separately from
              the forward delivery rate, which will be same as Forward Rates.
            </li>
            <li>
              The Merchant shall not book / handover or allows to be handed over
              any Product which is banned, restricted, illegal, prohibited,
              stolen, infringing of any third-party rights, liquid materials,
              hazardous or dangerous or in breach of any law or regulation in
              force in India for the purpose of the logistics or delivery
              services
            </li>
            <li>
              Other terms and conditions will be applicable as defined in the
              agreement.
            </li>
            <li>Above prices are inclusive of 15% FSC</li>
          </ul>
        </div>
      </div>
    </RightWrapper>
  );
};

export default RateChart;
